import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import { Loader2, Send, Mail, MessageSquare, Truck, Check, RefreshCw, Image as ImageIcon, Sparkles } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import PhotoUploader from '../../components/PhotoUploader';

const CHANNELS = [
  { id: 'email', label: 'Email', icon: Mail, sub: 'Free', cost: 0 },
  { id: 'sms', label: 'SMS', icon: MessageSquare, sub: '$0.02 each', cost: 0.02 },
  { id: 'mail', label: 'Printed postcard', icon: Truck, sub: '$1.10 each (incl. postage)', cost: 1.10 },
];

export default function SaveTheDate() {
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [guests, setGuests] = useState([]);
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGuestIds, setSelectedGuestIds] = useState([]);
  const [channel, setChannel] = useState('email');
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [t, c, g, s] = await Promise.all([
          client.get('/savethedate/templates'),
          client.get('/savethedate/config'),
          client.get('/guests'),
          client.get('/savethedate/sends'),
        ]);
        setTemplates(t.data);
        setConfig(c.data);
        setGuests(g.data);
        setSends(s.data);
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const updateConfig = async (patch) => {
    setConfig((prev) => ({ ...prev, ...patch }));
    try { await client.put('/savethedate/config', patch); } catch (e) { /* ignore */ }
  };

  const selectTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    updateConfig({ templateId: id, accent: t?.palette?.accent });
  };

  const activeTemplate = useMemo(
    () => templates.find((t) => t.id === config?.templateId) || templates[0],
    [templates, config?.templateId]
  );

  const toggleGuest = (id) => {
    setSelectedGuestIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelectedGuestIds(guests.map((g) => g.id));
  };

  const send = async () => {
    if (selectedGuestIds.length === 0) {
      toast({ title: 'Pick at least one guest.', description: 'Select recipients below before sending.' });
      return;
    }
    setSending(true);
    try {
      const { data } = await client.post('/savethedate/send', { guestIds: selectedGuestIds, channel });
      toast({
        title: `Sent to ${data.count} guests`,
        description: `Channel: ${data.channel}${data.estimatedCost > 0 ? ` · Est. cost $${data.estimatedCost.toFixed(2)}` : ' · free'}. (Mock mode — Lob/SendGrid wired up later.)`,
      });
      const updated = await client.get('/savethedate/sends');
      setSends(updated.data);
      setSelectedGuestIds([]);
    } catch (e) {
      toast({ title: 'Send failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  if (loading || !config) {
    return <div className="flex items-center gap-2 text-[#6A6A6A]"><Loader2 className="w-4 h-4 animate-spin" /> Loading Save the Date…</div>;
  }

  const estCost = (CHANNELS.find((c) => c.id === channel)?.cost || 0) * selectedGuestIds.length;

  return (
    <div className="animate-float-up">
      <div className="flex items-end justify-between mb-1">
        <div>
          <h1 className="font-serif text-4xl text-[#2A2A2A] flex items-center gap-3"><AppIcon feature="savethedate" size="lg" reveal testId="section-icon-savethedate" />Save the Date</h1>
          <p className="text-[#6A6A6A] mt-1">Design it. Preview it. Send it digitally or in the mail.</p>
        </div>
      </div>
      <div className="text-xs text-[#7B2C3B] bg-[#F4E4DA] inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-8 mt-3">
        <Sparkles className="w-3.5 h-3.5" /> Mock mode — real mailing wires up to Lob; email to SendGrid; SMS to Twilio.
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* LEFT: Templates + Editor */}
        <div className="xl:col-span-2 space-y-5">
          <div className="soft-card rounded-2xl p-5">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-4">1. Pick a template</h3>
            <div className="grid grid-cols-3 gap-3">
              {templates.map((t) => {
                const isActive = t.id === config.templateId;
                return (
                  <button
                    key={t.id}
                    onClick={() => selectTemplate(t.id)}
                    className={`relative rounded-xl overflow-hidden aspect-[4/3] border-2 transition-all ${isActive ? 'border-[#7B2C3B] scale-[1.02]' : 'border-transparent hover:border-[#EBD3C2]'}`}
                    style={{ background: t.palette.bg }}
                  >
                    <div className="absolute inset-0 flex flex-col items-center justify-center px-2 text-center">
                      <span className="font-serif text-[10px]" style={{ color: t.palette.ink }}>save the date</span>
                      <span className="font-serif text-sm mt-1" style={{ color: t.palette.ink }}>S &amp; J</span>
                      <span className="w-6 h-px my-1" style={{ background: t.palette.accent }} />
                      <span className="text-[8px]" style={{ color: t.palette.ink }}>06.14.26</span>
                    </div>
                    {isActive && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#7B2C3B] flex items-center justify-center">
                        <Check className="w-3 h-3 text-[#FBF7F2]" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-[#6A6A6A] mt-3 text-center font-serif italic">{activeTemplate?.name}</p>
          </div>

          <div className="soft-card rounded-2xl p-5">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-4">2. Fill in your details</h3>
            <div className="grid grid-cols-2 gap-3">
              <input value={config.partner1} onChange={(e) => updateConfig({ partner1: e.target.value })} placeholder="First name" className="px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]" />
              <input value={config.partner2} onChange={(e) => updateConfig({ partner2: e.target.value })} placeholder="Partner's name" className="px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]" />
            </div>
            <input type="date" value={config.weddingDate || ''} onChange={(e) => updateConfig({ weddingDate: e.target.value })} className="mt-3 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm text-[#3A3A3A] focus:outline-none focus:border-[#7B2C3B]" />
            <input value={config.venueShort} onChange={(e) => updateConfig({ venueShort: e.target.value })} placeholder="Venue (short)" className="mt-3 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]" />
            <input value={config.location} onChange={(e) => updateConfig({ location: e.target.value })} placeholder="City, State" className="mt-3 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]" />
            <textarea rows={3} value={config.message} onChange={(e) => updateConfig({ message: e.target.value })} placeholder="Message" className="mt-3 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B] resize-none" />
            <input value={config.photoUrl} onChange={(e) => updateConfig({ photoUrl: e.target.value })} placeholder="Photo URL (optional)" className="mt-3 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]" />
            <div className="mt-3">
              <PhotoUploader
                testId="std-photo"
                label="Or upload from your computer"
                aspect="wide"
                value={config.photoUrl}
                onChange={(url) => updateConfig({ photoUrl: url })}
              />
            </div>            <input value={config.rsvpUrl} onChange={(e) => updateConfig({ rsvpUrl: e.target.value })} placeholder="RSVP URL (optional)" className="mt-3 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]" />
          </div>
        </div>

        {/* MIDDLE: Live preview */}
        <div className="xl:col-span-2">
          <div className="soft-card rounded-2xl p-5 sticky top-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-[#2A2A2A]">3. Live preview</h3>
              <span className="text-xs text-[#6A6A6A] inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> auto-updates</span>
            </div>
            <PostcardPreview config={config} template={activeTemplate} />
          </div>
        </div>

        {/* RIGHT: Send */}
        <div className="xl:col-span-1 space-y-5">
          <div className="soft-card rounded-2xl p-5">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-4">4. How to send</h3>
            <div className="space-y-2">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const active = channel === c.id;
                return (
                  <button key={c.id} onClick={() => setChannel(c.id)} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${active ? 'border-[#7B2C3B] bg-[#FBF7F2]' : 'border-[#EFE3D8] hover:border-[#B8896C]'}`}>
                    <Icon className={`w-4 h-4 ${active ? 'text-[#7B2C3B]' : 'text-[#9A8A7E]'}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#2A2A2A]">{c.label}</p>
                      <p className="text-xs text-[#6A6A6A]">{c.sub}</p>
                    </div>
                    {active && <Check className="w-4 h-4 text-[#7B2C3B]" />}
                  </button>
                );
              })}
            </div>
            {channel === 'mail' && (() => {
              const n = (st) => guests.filter((g) => (g.address_status || 'missing') === st).length;
              return (
                <div data-testid="std-address-readiness" className="mt-3 rounded-xl bg-[#FBF7F2] border border-[#EFE3D8] p-3 text-xs text-[#3A3A3A] space-y-1">
                  <p><strong>{n('ok')}</strong> guests have a mailing-ready address · <span className="text-[#A15A2A]"><strong>{n('needs_fix')}</strong> need fixing</span> · <strong>{n('missing')}</strong> have none</p>
                  <p className="text-[#9A8A7E]">Fix addresses on the Guests page (open a guest → Mailing address). Real postcard mailing turns on once Lob is connected.</p>
                </div>
              );
            })()}
          </div>

          <div className="soft-card rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-xl text-[#2A2A2A]">5. Recipients</h3>
              <button onClick={selectAll} className="text-xs text-[#7B2C3B] hover:underline">Select all ({guests.length})</button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
              {guests.length === 0 ? (
                <p className="text-xs text-[#6A6A6A] py-4 text-center">Add guests to your list first.</p>
              ) : guests.map((g) => {
                const checked = selectedGuestIds.includes(g.id);
                return (
                  <label key={g.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#FBF7F2] cursor-pointer">
                    <input type="checkbox" checked={checked} onChange={() => toggleGuest(g.id)} className="accent-[#7B2C3B]" />
                    <span className="text-sm text-[#2A2A2A] flex-1">{g.name}</span>
                  </label>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-[#EFE3D8] text-xs text-[#6A6A6A] space-y-1">
              <div className="flex justify-between"><span>Selected</span><span className="text-[#2A2A2A]">{selectedGuestIds.length}</span></div>
              <div className="flex justify-between"><span>Estimated cost</span><span className="text-[#2A2A2A]">${estCost.toFixed(2)}</span></div>
            </div>

            <button onClick={send} disabled={sending || selectedGuestIds.length === 0} className="mt-4 w-full btn-primary py-3 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send to {selectedGuestIds.length || '0'}
            </button>
          </div>
        </div>
      </div>

      {/* Send history */}
      <div className="mt-8 soft-card rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-[#EFE3D8]">
          <h3 className="font-serif text-xl text-[#2A2A2A]">Send history</h3>
        </div>
        {sends.length === 0 ? (
          <p className="p-6 text-sm text-[#6A6A6A] text-center">No sends yet. Pick recipients above and hit Send.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FBF7F2] text-[#6A6A6A]">
                <tr><th className="text-left px-4 py-3">Recipient</th><th className="text-left px-4 py-3">Channel</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Sent</th></tr>
              </thead>
              <tbody>
                {sends.map((s) => (
                  <tr key={s.id} className="border-t border-[#EFE3D8]">
                    <td className="px-4 py-3 text-[#2A2A2A]">{s.name}</td>
                    <td className="px-4 py-3 text-[#3A3A3A] capitalize">{s.channel}</td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Sent (mock)</span></td>
                    <td className="px-4 py-3 text-[#6A6A6A] text-xs">{new Date(s.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PostcardPreview({ config, template }) {
  if (!template) return null;
  const { palette } = template;
  const formatDate = (d) => {
    if (!d) return '— · — · ——';
    try {
      const date = new Date(d);
      return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return d; }
  };
  return (
    <div className="space-y-3">
      {/* Front */}
      <div className="aspect-[3/2] rounded-2xl overflow-hidden shadow-md relative" style={{ background: palette.bg }}>
        {config.photoUrl && (
          <img src={config.photoUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
          <p className="text-[10px] uppercase tracking-[0.4em] mb-2" style={{ color: palette.ink }}>save the date</p>
          <p className="font-serif text-3xl lg:text-4xl leading-tight" style={{ color: palette.ink }}>
            {config.partner1 || 'You'} <span style={{ color: palette.accent }}>&amp;</span> {config.partner2 || 'Your love'}
          </p>
          <div className="w-16 h-px my-3" style={{ background: palette.accent }} />
          <p className="font-serif text-lg" style={{ color: palette.ink }}>{formatDate(config.weddingDate)}</p>
          {config.venueShort && <p className="text-xs mt-1" style={{ color: palette.ink, opacity: 0.8 }}>{config.venueShort}{config.location ? ` · ${config.location}` : ''}</p>}
        </div>
      </div>
      {/* Back */}
      <div className="aspect-[3/2] rounded-2xl overflow-hidden shadow-md p-6 flex flex-col justify-between" style={{ background: '#FBF7F2', border: `1px solid #EFE3D8` }}>
        <div>
          <p className="text-[9px] uppercase tracking-[0.4em] text-[#9A8A7E]">A note from us</p>
          <p className="font-serif text-sm mt-2 text-[#2A2A2A] leading-relaxed">{config.message || 'Add your message above.'}</p>
        </div>
        {config.rsvpUrl ? (
          <div className="text-xs text-[#7B2C3B]">RSVP: <span className="underline">{config.rsvpUrl}</span></div>
        ) : (
          <div className="text-xs text-[#9A8A7E] flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Add an RSVP URL to print it here</div>
        )}
      </div>
    </div>
  );
}
